import type { LocalVisit } from '@/site-visits/visitStore'

/**
 * The visit a survey works in.
 *
 * The id it records against, the ids the server has joined into that visit,
 * and whether this phone started it and has not heard back. The field types
 * come from {@link LocalVisit} — the phone's own copy of a visit — so the
 * tree survey and the irrigation survey cannot grow different ideas of one.
 * `pending` is false for a visit read back from the server. It is required,
 * so a caller cannot leave it out and have the outbox guess.
 */
export type SurveyVisit = {
  id: LocalVisit['id']
  joinedIds: readonly LocalVisit['joinedIds'][number][]
  pending: LocalVisit['pending']
}

/**
 * Whether an answer from the server is for the visit being worked in.
 *
 * A visit another phone started on the same day is joined into this one, and
 * the server may still answer under the id that phone made. Both are this
 * visit. Null on both sides is "no visit open".
 */
export function isForVisit(
  answer: string | null,
  visit: SurveyVisit | null,
): boolean {
  if (visit === null) return answer === null
  return (
    answer === visit.id ||
    (answer !== null && visit.joinedIds.includes(answer))
  )
}

/**
 * A flush that did not land.
 *
 * `needsAttention` is the part that matters. A dropped connection clears
 * itself and the retry loop handles it; a refusal does not, and never will.
 * Without the distinction a permanently stuck queue looks the same as a phone
 * in a tunnel, so nobody goes looking.
 */
export interface SyncFailure {
  message: string
  /**
   * True when the server refused the batch rather than failing to answer.
   * Retrying will not help; somebody has to look.
   */
  needsAttention: boolean
}
